import React, { useEffect, useState } from 'react';
import { Button, message } from 'antd';
import { resumeManagedBilling } from 'api/insuranceBilling';
import type { ManagedBillingScopeReq, ManagedBillingVo } from 'types/insuranceBilling';
import ConfirmModal from './ConfirmModal';
import ProviderPicker from './ProviderPicker';
import s from './s.module.less';

type Props = {
    // 非 null 即打开
    ehr: string | null;
    state: ManagedBillingVo;
    isGroup: boolean;
    selfProviderId?: number;
    onClose: () => void;
    onDone: (next: ManagedBillingVo) => void;
};

// 原型 confirmRestore：单个 EHR 行的 Turn on。group owner 选哪些 provider 恢复
//（默认当时被停的人全选：EHR 对所有人关掉 = 全部 provider，否则 = offForProviderIds）
export default function RestoreEhrModal({ ehr, state, isGroup, selfProviderId, onClose, onDone }: Props) {
    const row = state.ehrs.find((e) => e.ehr === ehr);
    const offHere = row ? state.providers.filter((p) => !row.on || row.offForProviderIds.includes(p.id)) : [];
    const group = isGroup && offHere.length > 0;
    const [pick, setPick] = useState<number[]>([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (ehr) setPick(offHere.map((p) => p.id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ehr]);

    const handleSubmit = async () => {
        if (!ehr || (group && !pick.length)) return;
        // 当时被停的人全部恢复 → 整个 EHR 恢复；只恢复一部分 → 带 providerIds，其余保持关闭
        const req: ManagedBillingScopeReq = {
            scope: 'ehrs',
            ehrs: [ehr],
            providerIds: group && pick.length < offHere.length ? pick : undefined,
        };
        setSubmitting(true);
        const res: any = await resumeManagedBilling(req);
        setSubmitting(false);
        if (res?.error) return;
        message.success('Managed billing turned on');
        onDone(res?.data?.data);
    };

    return (
        <ConfirmModal
            open={!!ehr}
            title={`Turn billing back on for ${ehr || ''}?`}
            onClose={onClose}
            footer={
                <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" disabled={group && !pick.length} loading={submitting} onClick={handleSubmit}>
                        Turn on
                    </Button>
                </>
            }
        >
            <p className={s.p}>
                Klarity starts pulling {ehr} visits and creating claims again from today. Visits from while it was off
                are not billed automatically; contact us if you need those reviewed.
            </p>
            {group && (
                <>
                    <div className={s.label}>For which providers</div>
                    <ProviderPicker
                        providers={offHere}
                        value={pick}
                        onChange={setPick}
                        selfProviderId={selfProviderId}
                    />
                </>
            )}
        </ConfirmModal>
    );
}
